import { useCallback, useRef } from 'react';
import type { PointerEvent as ReactPointerEvent, RefObject } from 'react';

const ROW_HSCROLL_DRAG_THRESHOLD_MOUSE_PX = 8;
const ROW_HSCROLL_DRAG_THRESHOLD_TOUCH_PX = 14;

function rowHScrollDragThresholdPx(pointerType: string): number {
  return pointerType === 'touch' ? ROW_HSCROLL_DRAG_THRESHOLD_TOUCH_PX : ROW_HSCROLL_DRAG_THRESHOLD_MOUSE_PX;
}

const QUICK_CREATE_ROW_SELECTOR = 'tr[data-wp-datatable-row="quick-create"]';

export function isDataTableRowPointerBlocked(target: EventTarget | null): boolean {
  if (!target || !(target instanceof Element)) return true;
  return Boolean(
    target.closest(
      [
        '[data-datatable-rowclick-ignore]',
        '[data-datatable-col-resize]',
        '[data-datatable-dnd-handle]',
        'button',
        'a',
        'input',
        'select',
        'textarea',
        'label',
        '[role="menu"]',
        '[role="menuitem"]',
        '[contenteditable="true"]',
      ].join(', '),
    ),
  );
}

export function consumeDataTableRowClickSuppression(suppressRef: { current: boolean }): boolean {
  if (suppressRef.current) {
    suppressRef.current = false;
    return true;
  }
  return false;
}

export function useDataTableRowHorizontalScroll(
  scrollRegionRef: RefObject<HTMLDivElement | null>,
  enabled: boolean,
) {
  const suppressRowClickRef = useRef(false);
  const dragStateRef = useRef<{
    pointerId: number;
    pointerType: string;
    startX: number;
    startY: number;
    startScrollLeft: number;
    dragging: boolean;
    captureTarget: HTMLTableSectionElement | null;
  } | null>(null);

  const clearDraggingAttr = useCallback(() => {
    const region = scrollRegionRef.current;
    if (region?.dataset.wpDatatableRowScrollDragging) {
      delete region.dataset.wpDatatableRowScrollDragging;
    }
  }, [scrollRegionRef]);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLTableSectionElement>) => {
      if (!enabled || e.button !== 0) return;
      const region = scrollRegionRef.current;
      if (!region) return;
      const row = (e.target as Element).closest('tr');
      if (!row || !row.parentElement || row.parentElement.tagName !== 'TBODY') return;
      if (row.matches(QUICK_CREATE_ROW_SELECTOR)) return;
      if (isDataTableRowPointerBlocked(e.target)) return;

      dragStateRef.current = {
        pointerId: e.pointerId,
        pointerType: e.pointerType,
        startX: e.clientX,
        startY: e.clientY,
        startScrollLeft: region.scrollLeft,
        dragging: false,
        captureTarget: null,
      };
      /* No capture on down — tap must still fire row click; capture only after horizontal drag starts. */
    },
    [enabled, scrollRegionRef],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLTableSectionElement>) => {
      const state = dragStateRef.current;
      if (!state || e.pointerId !== state.pointerId) return;
      const region = scrollRegionRef.current;
      if (!region) return;

      const dx = e.clientX - state.startX;
      const dy = e.clientY - state.startY;
      const threshold = rowHScrollDragThresholdPx(state.pointerType);
      if (!state.dragging) {
        if (Math.abs(dx) < threshold) return;
        /* Vertical finger movement = scroll page, not pan columns — leave click alone. */
        if (Math.abs(dx) <= Math.abs(dy)) return;
        state.dragging = true;
        region.dataset.wpDatatableRowScrollDragging = 'true';
        state.captureTarget = e.currentTarget;
        e.currentTarget.setPointerCapture(e.pointerId);
      }
      region.scrollLeft = state.startScrollLeft - dx;
      e.preventDefault();
    },
    [scrollRegionRef],
  );

  const finishPointer = useCallback(
    (e: ReactPointerEvent<HTMLTableSectionElement>) => {
      const state = dragStateRef.current;
      if (!state || e.pointerId !== state.pointerId) return;
      if (state.dragging) suppressRowClickRef.current = true;
      const captureTarget = state.captureTarget;
      dragStateRef.current = null;
      clearDraggingAttr();
      if (captureTarget?.hasPointerCapture(e.pointerId)) {
        captureTarget.releasePointerCapture(e.pointerId);
      }
    },
    [clearDraggingAttr],
  );

  const tbodyProps = enabled
    ? {
        onPointerDown,
        onPointerMove,
        onPointerUp: finishPointer,
        onPointerCancel: finishPointer,
      }
    : {};

  return { suppressRowClickRef, tbodyProps };
}
